import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Lock, Loader2 } from "lucide-react";

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function LoginPage() {
    const { status, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    if (status === "authed") {
        const dest = location.state?.from || "/admin";
        return <Navigate to={dest} replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!email || !password) {
            setError("Email and password are required");
            return;
        }
        setSubmitting(true);
        try {
            await login(email.trim().toLowerCase(), password);
            toast.success("Welcome back, Admin");
            navigate(location.state?.from || "/admin", { replace: true });
        } catch (err) {
            const msg = formatApiErrorDetail(
                err?.response?.data?.detail || err.message,
            );
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="min-h-screen bg-[#0A0A0A] text-white font-manrope flex flex-col"
            data-testid="login-page"
        >
            <Toaster theme="dark" position="bottom-right" />

            <header className="px-6 md:px-10 py-4 border-b border-white/10">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-zinc-400 hover:text-white"
                    data-testid="back-home"
                >
                    <ArrowLeft size={14} /> Back to map
                </Link>
            </header>

            <main className="flex-1 flex items-center justify-center px-6">
                <div className="w-full max-w-md border border-white/10 bg-[#0d0d0d] p-8">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF3B30] font-semibold mb-3 inline-flex items-center gap-1.5">
                        <Lock size={11} /> Admin Access
                    </div>
                    <h1 className="font-anton text-4xl uppercase mb-1 leading-tight">
                        Sign in to{" "}
                        <span className="text-[#FF3B30]">VA Lax HQ</span>
                    </h1>
                    <p className="text-xs text-zinc-500 mb-6">
                        Manage events, free of charge.
                    </p>

                    <form
                        onSubmit={submit}
                        className="space-y-4"
                        data-testid="login-form"
                    >
                        <div>
                            <Label className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-1.5 block">
                                Email
                            </Label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="rounded-none bg-black/40 border-white/15 focus-visible:ring-0 focus-visible:border-[#FF3B30]"
                                data-testid="login-email"
                                autoComplete="username"
                                autoFocus
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-1.5 block">
                                Password
                            </Label>
                            <Input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="rounded-none bg-black/40 border-white/15 focus-visible:ring-0 focus-visible:border-[#FF3B30]"
                                data-testid="login-password"
                                autoComplete="current-password"
                            />
                        </div>

                        {error && (
                            <div
                                className="border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-[#FF3B30]"
                                data-testid="login-error"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-[#FF3B30] hover:bg-[#FF574D] disabled:opacity-50 disabled:cursor-wait text-white font-anton tracking-widest uppercase text-base px-6 py-3 transition-colors flex items-center justify-center gap-2"
                            data-testid="login-submit"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={14} className="animate-spin" />
                                    Signing in
                                </>
                            ) : (
                                "Sign in"
                            )}
                        </button>
                    </form>
                </div>
            </main>
        </div>
    );
}
